import { useEffect, useMemo, useRef, useState, type DragEvent, type ReactNode } from 'react';
import {
  AlertCircle,
  CalendarDays,
  CheckCircle2,
  FileText,
  Info,
  Loader2,
  LockKeyhole,
  Mail,
  MessageCircle,
  UploadCloud,
  UserRound,
  XCircle,
} from 'lucide-react';
import api from '../../src/api';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';

type RevisionScreen = 'EDITABLE' | 'SUCCESS' | 'UNAVAILABLE';

interface RevisionUploadPolicy {
  maxSizeBytes: number;
  allowedExtensions: string[];
  accept: string;
}

interface RevisionPortalData {
  screen: RevisionScreen;
  eventName: string;
  folio: string;
  role: 'Ponente';
  maskedEmail: string;
  status: string;
  observations: string;
  canUpload: boolean;
  uploadPolicy: RevisionUploadPolicy;
  latestRevision: {
    versionNumber: number;
    uploadedAt: string;
    archivoNombre: string;
  } | null;
}

interface SpeakerRevisionPortalProps {
  token: string;
}

type UploadState = 'READY' | 'UPLOADING' | 'SUCCESS' | 'ERROR';

function formatBytes(bytes: number): string {
  return `${Math.round(bytes / 1024 / 1024)} MB`;
}

function formatDate(value: string | Date): string {
  return new Intl.DateTimeFormat('es-MX', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

function statusLabel(portalData: RevisionPortalData, uploadState: UploadState): string {
  if (uploadState === 'ERROR') return 'Corrección solicitada';
  if (portalData.screen === 'SUCCESS') return 'Versión corregida recibida';
  if (portalData.screen === 'UNAVAILABLE') return 'Enlace expirado';
  return 'Corrección solicitada';
}

function InfoRow({
  icon,
  label,
  children,
}: {
  icon: ReactNode;
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="grid grid-cols-[22px_92px_1fr] items-start gap-2 text-sm">
      <span className="mt-0.5 text-[#43546b]">{icon}</span>
      <span className="font-semibold text-[#43546b]">{label}:</span>
      <span className="font-medium text-[#0f2744]">{children}</span>
    </div>
  );
}

function DataPanel({ portalData, uploadState }: { portalData: RevisionPortalData; uploadState: UploadState }) {
  return (
    <section className="rounded-lg bg-[#eef2f6] px-5 py-4">
      <div className="space-y-3">
        <InfoRow icon={<CalendarDays className="h-4 w-4" />} label="Evento">
          {portalData.eventName}
        </InfoRow>
        <InfoRow icon={<FileText className="h-4 w-4" />} label="Folio">
          <span className="font-bold">{portalData.folio}</span>
        </InfoRow>
        <InfoRow icon={<UserRound className="h-4 w-4" />} label="Rol">
          {portalData.role}
        </InfoRow>
        <InfoRow icon={<Mail className="h-4 w-4" />} label="Correo">
          <span className="font-bold">{portalData.maskedEmail}</span>
        </InfoRow>
        <InfoRow icon={<CheckCircle2 className="h-4 w-4" />} label="Estado">
          <Badge className="border border-[#dfc15c] bg-[#f5dc83] px-3 py-0.5 text-[#5f4a10] hover:bg-[#f5dc83]">
            {statusLabel(portalData, uploadState)}
          </Badge>
        </InfoRow>
      </div>
    </section>
  );
}

export function SpeakerRevisionPortal({ token }: SpeakerRevisionPortalProps) {
  const [loading, setLoading] = useState(true);
  const [portalData, setPortalData] = useState<RevisionPortalData | null>(null);
  const [error, setError] = useState('');
  const [uploadState, setUploadState] = useState<UploadState>('READY');
  const [uploadError, setUploadError] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const loadPortal = async () => {
      try {
        const { data } = await api.get(`/speaker/revision/portal/${encodeURIComponent(token)}`);
        setPortalData(data);
        setError('');
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Acceso no disponible para cargar correcciones.');
      } finally {
        setLoading(false);
      }
    };

    if (!token) {
      setError('Acceso no disponible para cargar correcciones.');
      setLoading(false);
      return;
    }

    loadPortal();
  }, [token]);

  const allowedText = useMemo(() => {
    return portalData?.uploadPolicy.allowedExtensions.join(', ') || '.doc, .docx';
  }, [portalData?.uploadPolicy.allowedExtensions]);

  const validateFile = (file: File): string | null => {
    if (!portalData) return 'No fue posible validar el archivo.';
    const extension = `.${file.name.split('.').pop()?.toLowerCase() || ''}`;
    const nameParts = file.name.split('.').filter(Boolean);

    if (nameParts.length > 2) {
      return 'El nombre del archivo no debe contener doble extensión.';
    }

    if (!portalData.uploadPolicy.allowedExtensions.includes(extension)) {
      return `Formato no permitido. Usa ${allowedText}.`;
    }

    if (file.size > portalData.uploadPolicy.maxSizeBytes) {
      return `El archivo no debe exceder ${formatBytes(portalData.uploadPolicy.maxSizeBytes)}.`;
    }

    return null;
  };

  const selectFile = (file: File | null) => {
    if (!file) return;
    const validation = validateFile(file);
    if (validation) {
      setSelectedFile(null);
      setUploadError(validation);
      setUploadState('ERROR');
      return;
    }

    setSelectedFile(file);
    setUploadError('');
    setUploadState('READY');
  };

  const handleDrop = (event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDragOver(false);
    selectFile(event.dataTransfer.files?.[0] || null);
  };

  const uploadRevision = async () => {
    if (!selectedFile || !portalData?.canUpload) return;
    setUploadState('UPLOADING');
    setUploadError('');

    try {
      const formData = new FormData();
      formData.append('archivo', selectedFile);
      const { data } = await api.post(`/speaker/revision/upload/${encodeURIComponent(token)}`, formData);
      setPortalData((current) => current
        ? {
            ...current,
            screen: 'SUCCESS',
            canUpload: false,
            status: 'PENDING_REVIEW',
            latestRevision: data?.version || null,
          }
        : current);
      setUploadState('SUCCESS');
      setSelectedFile(null);
      if (inputRef.current) inputRef.current.value = '';
    } catch (err: any) {
      setUploadState('ERROR');
      setUploadError(err?.response?.data?.message || 'No fue posible cargar la versión corregida.');
    }
  };

  if (loading) {
    return (
      <div className="mx-auto flex min-h-[55vh] max-w-2xl items-center justify-center py-10">
        <Loader2 className="h-6 w-6 animate-spin text-[#0f2744]" />
      </div>
    );
  }

  if (error || !portalData) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-2">
        <Card className="overflow-hidden border-0 shadow-xl">
          <div className="bg-[#173b63] px-6 py-4 text-white">
            <p className="text-lg font-semibold">Subir ponencia</p>
          </div>
          <CardContent className="space-y-5 px-7 py-7">
            <div className="flex items-start gap-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border-4 border-[#ead9a7] text-[#c9a227]">
                <LockKeyhole className="h-7 w-7" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-[#0f2744]">Acceso no disponible</h1>
                <p className="mt-1 text-sm font-semibold text-slate-600">
                  {error || 'Este enlace ya no está disponible.'}
                </p>
              </div>
            </div>
            <Button className="w-full bg-[#dfc15c] font-semibold text-white hover:bg-[#c9a227]" onClick={() => { window.location.href = '/'; }}>
              Volver al inicio
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isSuccess = portalData.screen === 'SUCCESS';
  const isUnavailable = portalData.screen === 'UNAVAILABLE';
  const hasUploadError = uploadState === 'ERROR';

  return (
    <div className="mx-auto max-w-2xl px-4 py-2">
      <Card className="overflow-hidden border-0 bg-white shadow-xl">
        <div className="bg-[#173b63] px-6 py-4 text-white">
          <p className="text-lg font-semibold">Subir ponencia</p>
        </div>

        <CardContent className="space-y-5 px-7 py-7">
          {!isSuccess && !isUnavailable && !hasUploadError ? (
            <div>
              <h1 className="text-2xl font-bold text-[#0f2744]">Tu ponencia fue revisada</h1>
              <p className="mt-2 text-sm font-semibold leading-5 text-slate-600">
                El comité evaluador analizó tu propuesta y emitió observaciones. Para continuar con el proceso, revisa
                los comentarios y sube una nueva versión corregida.
              </p>
            </div>
          ) : null}

          {hasUploadError ? (
            <div className="flex items-start gap-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border-4 border-red-200 text-red-500">
                <AlertCircle className="h-8 w-8" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-[#0f2744]">No fue posible cargar el archivo</h1>
                <p className="mt-1 text-sm font-semibold leading-5 text-slate-600">
                  El archivo que seleccionaste no pudo ser aceptado. Corrige el problema indicado y vuelve a intentar subir tu versión corregida.
                </p>
              </div>
            </div>
          ) : null}

          {isSuccess ? (
            <div className="flex items-start gap-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border-4 border-green-200 text-green-500">
                <CheckCircle2 className="h-8 w-8" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-[#0f2744]">Tu versión corregida fue enviada</h1>
                <p className="mt-1 text-sm font-semibold leading-5 text-slate-600">
                  La nueva versión de tu ponencia se cargó correctamente. El comité evaluador revisará el archivo antes de continuar con el proceso.
                </p>
              </div>
            </div>
          ) : null}

          {isUnavailable ? (
            <div className="flex items-start gap-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border-4 border-[#ead9a7] text-[#c9a227]">
                <LockKeyhole className="h-7 w-7" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-[#0f2744]">Acceso no disponible</h1>
                <p className="mt-1 text-sm font-semibold leading-5 text-slate-600">
                  Este enlace ya no está disponible. La invitación para subir tu versión corregida ha expirado o ya no puede ser utilizada.
                </p>
              </div>
            </div>
          ) : null}

          <DataPanel portalData={portalData} uploadState={uploadState} />

          {!isSuccess && !isUnavailable ? (
            <section className="border-l-4 border-[#dfc15c] bg-[#fff8e8] px-5 py-4">
              <div className="flex items-start gap-3">
                <MessageCircle className="mt-0.5 h-5 w-5 shrink-0 text-[#43546b]" />
                <div>
                  <h2 className="font-bold text-[#0f2744]">Observaciones del comité evaluador</h2>
                  <p className="text-sm text-slate-600">Te compartimos los principales comentarios para mejorar tu ponencia:</p>
                  <p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-slate-700">{portalData.observations}</p>
                </div>
              </div>
            </section>
          ) : null}

          {portalData.canUpload ? (
            <label
              onDragOver={(event) => {
                event.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              className={`block cursor-pointer border-2 border-dashed px-8 py-6 text-center transition-colors ${
                hasUploadError
                  ? 'border-red-300 bg-red-50'
                  : dragOver
                    ? 'border-[#c9a227] bg-[#fff8e8]'
                    : 'border-slate-300 bg-white hover:border-[#c9a227] hover:bg-[#fffdf5]'
              }`}
            >
              <div className="flex flex-col items-center justify-center">
                <div className="relative">
                  <FileText className={`h-12 w-12 ${hasUploadError ? 'text-[#43546b]' : 'text-slate-400'}`} />
                  {hasUploadError ? (
                    <XCircle className="absolute -bottom-1 -right-2 h-6 w-6 rounded-full bg-red-50 text-red-500" />
                  ) : (
                    <UploadCloud className="absolute -bottom-1 -right-2 h-6 w-6 rounded-full bg-white text-slate-400" />
                  )}
                </div>
                <h3 className="mt-3 text-lg font-bold text-[#0f2744]">Subir versión corregida</h3>
                <p className="mt-2 text-sm text-slate-500">Arrastra y suelta un archivo aquí o haz clic para buscar</p>
                <Input
                  ref={inputRef}
                  type="file"
                  accept={portalData.uploadPolicy.accept}
                  className="sr-only"
                  onChange={(event) => selectFile(event.target.files?.[0] || null)}
                />
                <span className="mt-4 inline-flex rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-[#43546b]">
                  Adjuntar archivo
                </span>
                {selectedFile ? (
                  <p className="mt-3 text-sm text-slate-700">
                    Archivo seleccionado: <b>{selectedFile.name}</b>
                  </p>
                ) : null}
                {hasUploadError ? (
                  <p className="mt-3 text-sm font-semibold text-red-600">{uploadError}</p>
                ) : null}
                <p className="mt-2 text-xs text-slate-500">
                  Formatos permitidos: {allowedText}. Tamaño máximo: {formatBytes(portalData.uploadPolicy.maxSizeBytes)}.
                </p>
              </div>
            </label>
          ) : null}

          {isSuccess && portalData.latestRevision ? (
            <section className="bg-green-50 px-7 py-6 text-green-900">
              <div className="flex items-center gap-6">
                <div className="relative">
                  <FileText className="h-16 w-16 text-[#43546b]" />
                  <CheckCircle2 className="absolute -bottom-2 -right-2 h-8 w-8 rounded-full bg-green-50 text-green-500" />
                </div>
                <div className="space-y-2">
                  <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Archivo recibido</Badge>
                  <p className="text-lg font-bold text-[#0f2744]">{portalData.latestRevision.archivoNombre}</p>
                  <p className="text-sm">
                    Fecha y hora: <b>{formatDate(portalData.latestRevision.uploadedAt)}</b>
                  </p>
                  <p className="text-sm">Versión enviada: <b>v{portalData.latestRevision.versionNumber}</b></p>
                </div>
              </div>
            </section>
          ) : null}

          {isSuccess ? (
            <section className="bg-[#fff8e8] px-5 py-4">
              <div className="flex items-start gap-3">
                <Info className="mt-0.5 h-5 w-5 shrink-0 text-[#43546b]" />
                <div>
                  <h2 className="font-bold text-[#0f2744]">Siguiente paso</h2>
                  <p className="text-sm leading-5 text-slate-700">
                    El comité revisará tu nueva versión. Cuando el documento sea validado, el proceso de evaluación continuará conforme a las fechas del evento.
                  </p>
                </div>
              </div>
            </section>
          ) : null}

          {isUnavailable ? (
            <section className="border-l-4 border-[#dfc15c] bg-[#fff8e8] px-5 py-4">
              <div className="flex items-start gap-3">
                <Info className="mt-0.5 h-5 w-5 shrink-0 text-[#43546b]" />
                <div>
                  <h2 className="font-bold text-[#0f2744]">Este acceso ya no permite subir archivos</h2>
                  <p className="mt-2 text-sm leading-6 text-slate-700">
                    La carga ya fue atendida o el enlace no está disponible. Vuelve al inicio para continuar.
                  </p>
                </div>
              </div>
            </section>
          ) : null}

          <Button
            className="w-full bg-[#dfc15c] font-semibold text-white hover:bg-[#c9a227]"
            onClick={portalData.canUpload ? uploadRevision : () => { window.location.href = '/'; }}
            disabled={portalData.canUpload && (!selectedFile || uploadState === 'UPLOADING')}
          >
            {uploadState === 'UPLOADING' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {portalData.canUpload
              ? hasUploadError ? 'Intentar nuevamente' : 'Enviar versión corregida'
              : isSuccess ? 'Entendido' : 'Volver al inicio'}
          </Button>

          <p className="text-center text-xs font-medium text-slate-400">
            {isSuccess
              ? 'Conserva este correo como referencia. Si el comité requiere información adicional, te notificaremos por los canales institucionales.'
              : 'La nueva versión será revisada por el comité antes de continuar con el proceso de evaluación.'}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
